import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
} from '@mui/material';
import {
  MenuBook as MenuBookIcon,
  PostAdd as PostAddIcon,
  Instagram as InstagramIcon,
  Image as ImageIcon,
  GroupWork as GroupWorkIcon,
  AutoAwesome as AutoAwesomeIcon,
  Analytics as AnalyticsIcon,
  Business as BusinessIcon,
  Campaign as CampaignIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { isDocTopicVisible, SHOW_INSTAGRAM_UI, SHOW_INSTAGRAM_AS_PUBLISH_TARGET } from '../config/featureFlags';

const postingDocDescription = SHOW_INSTAGRAM_AS_PUBLISH_TARGET
  ? 'Create and schedule posts across Facebook, Instagram, LinkedIn, Tumblr, YouTube, and Pinterest.'
  : 'Create and schedule posts across Facebook, LinkedIn, Tumblr, YouTube, and Pinterest. Instagram feed posts go out when you publish to Facebook with a linked Instagram Business account.';

const docConnectPlatformsLine = SHOW_INSTAGRAM_AS_PUBLISH_TARGET
  ? 'Click Connect for each platform you want to use (Facebook, Instagram, LinkedIn, Tumblr, YouTube, Pinterest).'
  : 'On Clients, connect Facebook (and Link Instagram on the Page for dual-publish), LinkedIn, Tumblr, YouTube, and Pinterest.';

const docSupportedPlatformsLine = SHOW_INSTAGRAM_AS_PUBLISH_TARGET
  ? 'Facebook, Instagram, LinkedIn, Tumblr, YouTube, and Pinterest are fully supported for posting and scheduling.'
  : 'Facebook, LinkedIn, Tumblr, YouTube, and Pinterest are supported for posting and scheduling. Instagram feed posts use Facebook Page → linked Instagram Business dual-publish.';

const docPostingSelectorLine = SHOW_INSTAGRAM_AS_PUBLISH_TARGET
  ? 'Add a caption. Use the platform selector to choose where to publish (Facebook, Instagram, LinkedIn, Tumblr, YouTube, Pinterest).'
  : 'Add a caption. Choose Facebook (and other platforms) to publish. For Instagram feed reach, link Instagram on the client’s Facebook Page — dual-publish runs with the Facebook post.';

const ALL_DOC_SECTIONS = [
  { id: 'getting-started', title: 'Getting Started', description: 'Setup, connections, and platform authentication.', icon: MenuBookIcon, path: '/docs/getting-started', keywords: 'connect oauth token setup install' },
  { id: 'posting', title: 'Posting', description: postingDocDescription, icon: PostAddIcon, path: '/docs/posting', keywords: 'create publish schedule pinterest board' },
  { id: 'instagram-automation', title: 'Instagram Automation', description: 'Follow/unfollow, get followers, follow from network, and growth tools.', icon: InstagramIcon, path: '/docs/instagram-automation', keywords: 'follow unfollow puppeteer proxy' },
  { id: 'image-directories', title: 'Image Directories', description: 'Post to 500px and DeviantArt.', icon: ImageIcon, path: '/docs/image-directories', keywords: '500px deviantart portfolio' },
  { id: 'group-sharing', title: 'Group Sharing', description: 'Share Page posts to Facebook groups automatically.', icon: GroupWorkIcon, path: '/docs/group-sharing', keywords: 'facebook groups share page' },
  { id: 'ai-content', title: 'AI Content', description: 'AI-powered captions, hashtags, and content generation.', icon: AutoAwesomeIcon, path: '/docs/ai-content', keywords: 'captions hashtags generate' },
  { id: 'analytics', title: 'Analytics', description: 'Performance metrics and insights across platforms.', icon: AnalyticsIcon, path: '/docs/analytics', keywords: 'metrics insights performance' },
  { id: 'competitors', title: 'Competitors', description: 'Track competitor accounts and analyze their content.', icon: BusinessIcon, path: '/docs/competitors', keywords: 'competitor benchmark track' },
  { id: 'campaigns', title: 'Campaigns', description: 'What a campaign is, 3-step create, and what AI recommendations produce.', icon: CampaignIcon, path: '/docs/campaigns', keywords: 'campaign plan create ai advanced' },
  { id: 'clients', title: 'Clients', description: 'Manage client accounts and access.', icon: PeopleIcon, path: '/docs/clients', keywords: 'client account switch' },
  { id: 'settings', title: 'Settings', description: 'Account preferences, tokens, and configuration.', icon: SettingsIcon, path: '/docs/settings', keywords: 'settings tokens reconnect' },
];

const docSections = ALL_DOC_SECTIONS.filter((s) =>
  isDocTopicVisible(s.id),
);

const topicContent = {
  'getting-started': {
    title: 'Getting Started',
    body: [
      {
        id: 'connect',
        heading: 'Connect your platforms',
        content: [
          'Go to Settings in the sidebar, then open the Platform Accounts section.',
          docConnectPlatformsLine,
          'You\'ll be redirected to the platform\'s OAuth page. Sign in and authorize Dynamic Nexus to access your account.',
          'After authorization, you\'ll return to the app. The connection status will show as connected with a green indicator.',
          'For Facebook: connect a Page, not just a personal profile. The Page is required for posting.',
        ],
      },
      {
        id: 'platforms',
        heading: 'Supported platforms',
        content: [
          docSupportedPlatformsLine,
          'X (Twitter) is not yet implemented; it requires API access and is planned for a future release.',
          'Some platforms use browser automation (Puppeteer) for posting; others use official APIs.',
        ],
      },
      {
        id: 'tokens',
        heading: 'Token management',
        content: [
          'Tokens are stored securely on the backend. You do not need to manage them manually.',
          'If a platform shows "disconnected" or posts fail, go to Settings → Platform Accounts and click Reconnect for that platform.',
          'Some platforms require periodic re-authentication. Reconnect when prompted or when actions fail.',
        ],
      },
      {
        id: 'tips',
        heading: 'Quick tips',
        content: [
          'Connect all platforms you plan to use before creating posts. Platform-specific options (e.g. Pinterest board) appear only when the platform is connected.',
          'Use the Dashboard to see connection status at a glance.',
        ],
      },
    ],
  },
  posting: {
    title: 'Posting',
    body: [
      {
        id: 'create',
        heading: 'Create a post',
        content: [
          'Navigate to Posts in the sidebar, then click Create Post.',
          'Upload media (image or video). Supported formats depend on the platform; JPG/PNG and MP4 work for most.',
          docPostingSelectorLine,
          'Each selected platform may show extra fields in Basic Information (e.g. Pinterest Link and Board).',
          'Click Publish Now to post immediately, or choose Schedule to set a future date and time.',
        ],
      },
      {
        id: 'platform-opts',
        heading: 'Platform-specific options',
        content: [
          'Pinterest: requires a link URL and board name. The board name must match an existing board on your Pinterest account.',
          'YouTube: add video title and description in the Basic Information section.',
          'Facebook: optionally select a Page if you have multiple Pages.',
        ],
      },
      {
        id: 'pinterest',
        heading: 'Pinterest details',
        content: [
          'Pinterest posting uses a headless browser. Enter the exact board name as it appears on Pinterest.',
          'The board is selected during the publish flow (after the image is uploaded). If the name doesn\'t match, the post may fail.',
          'Set PINTEREST_POST_HEADLESS=false in your environment to see the browser window for debugging.',
        ],
      },
      {
        id: 'scheduling',
        heading: 'Scheduling',
        content: [
          'Scheduled posts are queued in the backend and published at the specified time. Ensure your server and workers are running.',
          'Timezone is based on your account or server configuration. Check Settings if times look incorrect.',
        ],
      },
      {
        id: 'troubleshoot',
        heading: 'Troubleshooting',
        content: [
          'If posting fails: check connection status in Settings, try reconnecting the platform, and review any error message in the UI.',
          'For Pinterest: verify the board name is correct and that you\'re logged into Pinterest in the automation browser (or use correct credentials).',
        ],
      },
    ],
  },
  'instagram-automation': {
    title: 'Instagram Automation',
    body: [
      {
        id: 'actions',
        heading: 'Available actions',
        content: [
          'Follow / Unfollow: Follow or unfollow a list of users (e.g. from a CSV or text list).',
          'Follow from network: Enter a target username; the tool follows users from their followers or following list.',
          'Get followers/following: Export your followers or following list.',
          'Unfollow targets: Unfollow users from a list.',
          'Remove non-mutuals: Unfollow users who don\'t follow you back.',
        ],
      },
      {
        id: 'how',
        heading: 'How it works',
        content: [
          'Automation uses Puppeteer (headless Chrome) to control the Instagram web interface.',
          'You need valid Instagram credentials. The tool logs in and performs actions in real time.',
          'Rates are limited to avoid blocks. Avoid running too many actions in a short period.',
        ],
      },
      {
        id: 'proxies',
        heading: 'Proxies and reliability',
        content: [
          'Datacenter IPs (e.g. VPS, cloud) are often blocked by Instagram. For 24/7 automation on a server, use residential proxies (Bright Data, Oxylabs, etc.).',
          'Tunnels can help in some setups but residential proxies are recommended for production.',
        ],
      },
      {
        id: 'practices',
        heading: 'Best practices',
        content: [
          'Start with small daily limits. Gradually increase if accounts remain in good standing.',
          'Mix actions (follow + unfollow + likes) rather than only following. Instagram flags aggressive follow-only behavior.',
        ],
      },
    ],
  },
  'image-directories': {
    title: 'Image Directories',
    sections: [
      { heading: 'Supported sites', id: 'sites' },
      { heading: 'How to post', id: 'how' },
    ],
    body: [
      { id: 'sites', heading: 'Supported sites', content: ['500px and DeviantArt. Upload images with titles and descriptions to grow your portfolio.'] },
      { id: 'how', heading: 'How to post', content: ['Go to Image Directories, select a site, upload your image, add a title and description, and publish. Each site has its own auth flow — connect via Settings if needed.'] },
    ],
  },
  'group-sharing': {
    title: 'Group Sharing',
    sections: [
      { heading: 'Overview', id: 'overview' },
      { heading: 'Setup', id: 'setup' },
    ],
    body: [
      { id: 'overview', heading: 'Overview', content: ['Share your Facebook Page posts to selected Facebook groups automatically. Useful for amplifying reach without manual sharing.'] },
      { id: 'setup', heading: 'Setup', content: ['Connect your Facebook Page (Settings → Platform Accounts). Go to Group Sharing, select your Page, then add the groups you want to share to. Configure sharing rules (e.g. share all posts or only specific ones).'] },
    ],
  },
  'ai-content': {
    title: 'AI Content',
    sections: [
      { heading: 'What you can generate', id: 'generate' },
      { heading: 'Using the AI Content page', id: 'using' },
    ],
    body: [
      { id: 'generate', heading: 'What you can generate', content: ['Captions, hashtags, post ideas, and variations. Uses AI to suggest engaging copy based on your input (topic, tone, platform).'] },
      { id: 'using', heading: 'Using the AI Content page', content: ['Open AI Content in the sidebar. Enter a prompt or topic, choose output type (caption, hashtags, etc.), and generate. You can copy results into the Create Post flow.'] },
    ],
  },
  analytics: {
    title: 'Analytics',
    sections: [
      { heading: 'Overview', id: 'overview' },
      { heading: 'Data sources', id: 'sources' },
    ],
    body: [
      { id: 'overview', heading: 'Overview', content: ['View performance metrics (likes, comments, shares, reach) across connected platforms. Compare over time and by platform.'] },
      { id: 'sources', heading: 'Data sources', content: ['Data is pulled from each platform\'s API. Ensure platforms are connected. Some metrics may have delays or limited history depending on the API.'] },
    ],
  },
  competitors: {
    title: 'Competitors',
    sections: [
      { heading: 'Adding competitors', id: 'add' },
      { heading: 'What you get', id: 'data' },
    ],
    body: [
      { id: 'add', heading: 'Adding competitors', content: [SHOW_INSTAGRAM_UI ? 'Go to Competitors, add competitor account handles (e.g. Instagram username). The app will scrape or fetch their public data where possible.' : 'Go to Competitors, add competitor account handles. The app will scrape or fetch their public data where possible.'] },
      { id: 'data', heading: 'What you get', content: ['Post frequency, engagement metrics, content themes. Use for benchmarking and strategy. ML Insights provides deeper analysis.'] },
    ],
  },
  campaigns: {
    title: 'Campaigns',
    sections: [
      { heading: 'What a campaign is', id: 'overview' },
      { heading: '3-step create', id: 'create' },
      { heading: 'What AI produces', id: 'ai' },
      { heading: 'Activate and manage', id: 'manage' },
    ],
    body: [
      {
        id: 'overview',
        heading: 'What a campaign is',
        content: [
          'A campaign is a client-scoped plan: name, type (engagement, conversion, brand building, etc.), target audience, and status (draft, active, paused).',
          'Use campaigns to organize strategy and track published performance summaries for that plan.',
          'This is not Instagram Follow/Unfollow automation (that lives under Instagram Automation).',
        ],
      },
      {
        id: 'create',
        heading: '3-step create',
        content: [
          '1. Open Campaigns and click New Campaign (primary button).',
          '2. Enter name, optional description, campaign type, client, and target audience — then save.',
          '3. When ready, Activate the campaign from the card. Pause anytime; edit or delete from the same card.',
          'Skip Advanced unless you want optional AI recommendations.',
        ],
      },
      {
        id: 'ai',
        heading: 'What AI produces',
        content: [
          'Under Advanced → AI recommendations, you can request strategy suggestions for a client, type, audience, budget, and duration.',
          'Results typically include organic growth notes, platform strategies, and expected performance / projection estimates.',
          'These are AI-generated estimates — not live ads manager metrics or guaranteed outcomes.',
          'Create campaign with AI is optional and creates a draft from those recommendations; the normal path remains New Campaign.',
        ],
      },
      {
        id: 'manage',
        heading: 'Activate and manage',
        content: [
          'Cards show status, client, audience, and performance summary when available.',
          'Activate / Pause / Edit / Delete from each card. Prefer one clear campaign per client goal rather than many empty drafts.',
        ],
      },
    ],
  },
  clients: {
    title: 'Clients',
    sections: [
      { heading: 'Overview', id: 'overview' },
      { heading: 'Managing clients', id: 'manage' },
    ],
    body: [
      { id: 'overview', heading: 'Overview', content: ['Manage multiple client accounts. Each client can have their own platform connections and content.'] },
      { id: 'manage', heading: 'Managing clients', content: ['Add clients in the Clients section. Switch between clients via the client selector. Posting, analytics, and other features operate in the context of the selected client.'] },
    ],
  },
  settings: {
    title: 'Settings',
    sections: [
      { heading: 'Platform Accounts', id: 'platforms' },
      { heading: 'Account preferences', id: 'prefs' },
    ],
    body: [
      { id: 'platforms', heading: 'Platform Accounts', content: ['Connect, disconnect, or reconnect social platforms. Check token status and fix expired connections here.'] },
      { id: 'prefs', heading: 'Account preferences', content: ['Update profile, timezone, notifications, and other app settings.'] },
    ],
  },
};

function buildSearchIndex() {
  const index = [];
  docSections.forEach((s) => {
    index.push({ id: s.id, title: s.title, text: `${s.title} ${s.description} ${s.keywords}`.toLowerCase(), path: s.path });
  });
  Object.entries(topicContent).forEach(([id, t]) => {
    if (!isDocTopicVisible(id)) return;
    const text = [t.title, ...t.body.flatMap((b) => [b.heading, ...b.content])].join(' ').toLowerCase();
    index.push({ id, title: t.title, text, path: `/docs/${id}` });
  });
  return index;
}

const searchIndex = buildSearchIndex();

function DocSectionBlock({ section }) {
  return (
    <Box
      id={section.id}
      sx={{
        mb: 4,
        scrollMarginTop: 100,
      }}
    >
      <Typography
        component="h2"
        variant="h6"
        sx={{
          fontWeight: 600,
          color: 'primary.main',
          mb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <LinkIcon sx={{ fontSize: 18, opacity: 0.7 }} />
        {section.heading}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 1, lineHeight: 1.75, color: 'text.secondary' } }}>
        {section.content.map((paragraph, i) => (
          <li key={i}>{paragraph}</li>
        ))}
      </Box>
    </Box>
  );
}

function DocTopicView({ topicId, onBack }) {
  const content =
    topicId && isDocTopicVisible(topicId) ? topicContent[topicId] : undefined;
  const sections = content?.body?.map((b) => ({ heading: b.heading, id: b.id })) ?? [];

  if (!content) {
    return (
      <Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Topic not found. Choose one from the overview.
        </Typography>
        <Typography
          component="span"
          onClick={onBack}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'primary.main',
            cursor: 'pointer',
            fontSize: '0.95rem',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <ArrowBackIcon fontSize="small" /> Back to overview
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      <Box sx={{ flex: '1 1 580px', minWidth: 0 }}>
        <Typography
          component="span"
          onClick={onBack}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'primary.main',
            cursor: 'pointer',
            fontSize: '0.9rem',
            mb: 2,
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <ArrowBackIcon fontSize="small" /> Back to overview
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
          {content.title}
        </Typography>
        {content.body.map((section) => (
          <DocSectionBlock key={section.id} section={section} />
        ))}
      </Box>
      <Box
        sx={{
          flex: '0 0 200px',
          position: 'sticky',
          top: 100,
          height: 'fit-content',
        }}
      >
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 1 }}>
          On this page
        </Typography>
        <List dense disablePadding sx={{ mt: 1 }}>
          {sections.map((s) => (
            <ListItem key={s.id} disablePadding>
              <ListItemButton
                component="a"
                href={`#${s.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' });
                }}
                sx={{
                  py: 0.5,
                  fontSize: '0.9rem',
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {s.heading}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
}

function DocsHome({ onSelect, search, onSearch }) {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return docSections;
    return docSections.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        (s.keywords && s.keywords.toLowerCase().includes(q))
    );
  }, [search]);

  const contentMatches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || q.length < 2) return [];
    return searchIndex.filter((item) => item.text.includes(q) && !docSections.find((s) => s.id === item.id)?.title.toLowerCase().includes(q));
  }, [search]);

  const uniqueMatches = useMemo(() => {
    const seen = new Set();
    return contentMatches.filter((m) => {
      if (seen.has(m.id)) return false;
      if (filtered.some((f) => f.id === m.id)) return false;
      seen.add(m.id);
      return true;
    });
  }, [contentMatches, filtered]);

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Documentation
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 560, lineHeight: 1.7 }}>
          Guides and references for Dynamic Nexus. Search or browse the sections below.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <TextField
            placeholder="Search docs…"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 400,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255,255,255,0.04)',
                borderRadius: 2,
                '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' },
              },
            }}
          />
        </Box>
      </Box>

      {search.trim() && uniqueMatches.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
            Also found in
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {uniqueMatches.slice(0, 6).map((m) => (
              <Card
                key={m.path}
                sx={{
                  cursor: 'pointer',
                  border: '1px solid rgba(48, 54, 61, 0.6)',
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1,
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(88,166,255,0.06)' },
                }}
                onClick={() => onSelect(m.path)}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {m.title}
                </Typography>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
        {filtered.length} {filtered.length === 1 ? 'section' : 'sections'}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        {filtered.map((section) => {
          const Icon = section.icon;
          return (
            <Card
              key={section.id}
              sx={{
                flex: '0 1 calc(20% - 13px)',
                minWidth: 140,
                aspectRatio: 1,
                bgcolor: 'rgba(22, 27, 34, 0.8)',
                border: '1px solid rgba(48, 54, 61, 0.5)',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                },
              }}
            >
              <CardActionArea
                onClick={() => onSelect(section.path)}
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'stretch',
                }}
              >
                <CardContent
                  sx={{
                    p: 2,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                  }}
                >
                  <Box sx={{ color: 'primary.main', mb: 1, opacity: 0.9 }}>
                    <Icon sx={{ fontSize: 28 }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {section.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {section.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
      {filtered.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No sections match "{search}". Try a different term or browse all topics above.
        </Typography>
      )}
    </Box>
  );
}

export default function Docs() {
  const { topic } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const handleSelect = (path) => {
    navigate(path);
  };

  const handleBack = () => {
    navigate('/docs');
  };

  return (
    <Box
      sx={{
        maxWidth: 960,
        mx: 'auto',
        px: { xs: 2, sm: 3 },
        py: 4,
      }}
    >
      {topic ? (
        <DocTopicView topicId={topic} onBack={handleBack} />
      ) : (
        <DocsHome onSelect={handleSelect} search={search} onSearch={setSearch} />
      )}
    </Box>
  );
}
